import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AdminPageShellComponent } from '../../../shared/components/admin/admin-page-shell/admin-page-shell.component';
import { CtaButtonComponent } from '../../../shared/components/ui/cta-button/cta-button.component';
import { InfoBannerComponent } from '../../../shared/components/ui/info-banner/info-banner.component';
import { getValvurApiError } from '../../../core/errors/parse-valvur-api-error';
import { resolveDisclosureScopeLabel } from '../../identity/helpers/disclosure-scope.helper';
import { PartnerIntegrationRequest } from '../../patrol/models/partner-integration.model';
import { PartnerIntegrationReviewService } from './services/partner-integration-review.service';

@Component({
  selector: 'app-partner-integration-review',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    AdminPageShellComponent,
    CtaButtonComponent,
    InfoBannerComponent,
  ],
  templateUrl: './partner-integration-review.component.html',
  styleUrl: './partner-integration-review.component.scss',
})
export class PartnerIntegrationReviewComponent implements OnInit {
  #review = inject(PartnerIntegrationReviewService);
  #translate = inject(TranslateService);

  readonly requests = signal<PartnerIntegrationRequest[]>([]);
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly pageError = signal<string | null>(null);
  readonly actingRequestId = signal<string | null>(null);
  readonly notesByRequestId = signal<Record<string, string>>({});

  ngOnInit(): void {
    void this.load();
  }

  async load(): Promise<void> {
    this.loading.set(true);
    this.loadError.set(false);
    this.pageError.set(null);

    try {
      this.requests.set(await this.#review.listPending());
    } catch {
      this.loadError.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  scopeLabel(scopeId: string): string {
    return resolveDisclosureScopeLabel(scopeId, (key: string) =>
      this.#translate.instant(key),
    );
  }

  notesFor(requestId: string | undefined): string {
    if (!requestId) {
      return '';
    }
    return this.notesByRequestId()[requestId] ?? '';
  }

  updateNotes(requestId: string, value: string): void {
    this.notesByRequestId.update((current) => ({
      ...current,
      [requestId]: value,
    }));
  }

  isActing(requestId: string | undefined): boolean {
    return !!requestId && this.actingRequestId() === requestId;
  }

  async approve(request: PartnerIntegrationRequest): Promise<void> {
    if (!request.id) {
      return;
    }
    await this.runAction(request.id, () =>
      this.#review.approve(request.id!, {
        reviewerNotes: this.notesFor(request.id)?.trim() || undefined,
      }),
    );
  }

  async reject(request: PartnerIntegrationRequest): Promise<void> {
    if (!request.id) {
      return;
    }
    await this.runAction(request.id, () =>
      this.#review.reject(request.id!, {
        reviewerNotes: this.notesFor(request.id)?.trim() || undefined,
      }),
    );
  }

  async requestInfo(request: PartnerIntegrationRequest): Promise<void> {
    if (!request.id) {
      return;
    }

    const notes = this.notesFor(request.id)?.trim();
    if (!notes) {
      this.pageError.set(
        this.#translate.instant('app.admin.identity.integrationReview.notesRequired'),
      );
      return;
    }

    await this.runAction(request.id, () =>
      this.#review.requestInfo(request.id!, { reviewerNotes: notes }),
    );
  }

  private async runAction(
    requestId: string,
    action: () => Promise<PartnerIntegrationRequest>,
  ): Promise<void> {
    this.actingRequestId.set(requestId);
    this.pageError.set(null);

    try {
      await action();
      this.requests.update((items) =>
        items.filter((item) => item.id !== requestId),
      );
    } catch (error) {
      this.pageError.set(
        getValvurApiError(error)?.message ??
          this.#translate.instant('app.admin.identity.integrationReview.actionError'),
      );
    } finally {
      this.actingRequestId.set(null);
    }
  }
}
